import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  CheckCircle2, ChevronRight, ArrowRight, Zap, Activity,
  AlertTriangle, FileText, BarChart3, Battery, Sun, TrendingUp,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";
import useNoIndex from "@/lib/useNoIndex";
import BespokePageRibbon from "@/components/BespokePageRibbon";
import BespokeWatermark from "@/components/BespokeWatermark";

// Phase 25.16aw — Burditt bespoke proposal page.
//
// Trigger: Burditt Strategic Planning met with Manuel 2026-06-01. The
// Ventia industrial-connector demo didn't land — his clients are in
// energy, not industrial services. His sharp business question was:
// "my client consumes 43 million [kWh] at A$0.22/kWh, how much can he
// SELL it for?" — reframing energy as a revenue stream, not a cost.
//
// This page answers that question directly: shows the 5 NEM arbitrage
// avenues, links to a downloadable mock brief running the actual numbers
// on his client's 43 GWh / A$0.22/kWh / A$9.46M annual spend, and
// proposes the same handshake-first strategic partnership we agreed
// with Dayo (no contract, no commission cap, paper on the back end).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ARBITRAGE_AVENUES = [
  {
    Icon: AlertTriangle,
    code: "01",
    name: "RERT — Emergency Reserve",
    plain: "AEMO pays your client to reduce load during grid emergencies — short-notice curtailment events, 2-4 hours each, 5-15 times per year. Your client agrees in advance to shed up to 2 MW of flexible load and gets paid every time AEMO activates the program. Zero capex, register and forget.",
    revenue: "A$50,000 - A$300,000 / year",
    capex: "A$0",
    timeline: "3-6 weeks to register",
  },
  {
    Icon: Activity,
    code: "02",
    name: "FCAS — Ancillary Services",
    plain: "Frequency Control Ancillary Services. Your client's PLC bids small amounts of flexibility into the grid-stability markets every dispatch interval. Recurring revenue, runs continuously in the background once configured. The biggest steady contributor in the stack.",
    revenue: "A$130,000 - A$400,000 / year",
    capex: "A$30,000 - A$80,000",
    timeline: "6-10 weeks",
  },
  {
    Icon: TrendingUp,
    code: "03",
    name: "WDRM — Wholesale Demand Response",
    plain: "The newer market mechanism, fully launched in the NEM in 2021. Your client registers as a Demand Response Service Provider and earns spot-market-equivalent revenue every time they curtail during a price spike. Zero capex, retailer pre-approval required.",
    revenue: "A$80,000 - A$250,000 / year",
    capex: "A$0",
    timeline: "8-12 weeks",
  },
  {
    Icon: Sun,
    code: "04",
    name: "LGCs — Renewable Certificates",
    plain: "Only applicable if your client's PPA is renewable-backed. Each megawatt-hour of certified renewable consumption produces one Large-Scale Generation Certificate. LGCs trade at A$50-80 each. We audit the existing PPA terms and tell your client what they're sitting on.",
    revenue: "A$0 - A$200,000 / year",
    capex: "A$0",
    timeline: "immediate (after PPA audit)",
  },
  {
    Icon: Battery,
    code: "05",
    name: "BTM Generation — Solar + Battery",
    plain: "The capex-required avenue, but the biggest upside. Behind-the-meter solar + battery sized to peak-shave the client's own load AND export surplus to the grid during high-price windows. 5-7 year payback on conservative modelling. Combines with avenues 01-04 for stack effect.",
    revenue: "A$200,000 - A$600,000 / year",
    capex: "A$2.5M - A$4M",
    timeline: "9-14 months to revenue",
  },
];

export default function Burditt() {
  useNoIndex();
  usePageview();

  const [form, setForm] = useState({
    email: "",
    name: "Burditt",
    role: "Burditt Strategic Planning",
    focus: "Walk me through the math for my client (43 GWh / A$0.22)",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Work email is required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/industry-leads`, {
        email: form.email,
        name: form.name,
        company: "Burditt Strategic Planning",
        role: form.role,
        message: [
          form.focus ? `Focus: ${form.focus}` : "",
          form.message ? `Notes: ${form.message}` : "",
        ].filter(Boolean).join("\n"),
        industry: "burditt",
        source: "burditt-bespoke",
        visitor_session: getVisitorSession(),
      });
      setSubmitted(true);
      toast.success("Received — Manuel will reach out within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="burditt-page">
      <BespokePageRibbon prospectName="Burditt Strategic Planning" />
      <BespokeWatermark pageLabel="BURDITT" position="top" />

      {/* Top nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="burditt-back-to-home">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 ml-2">
              · A NOTE FOR BURDITT
            </span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <a href="#engage" data-testid="burditt-nav-engage" className="bg-[#FFB000] text-black font-bold font-mono text-[11px] sm:text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90">
              Reply to Manuel
            </a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#10283F]/40 via-transparent to-[#FFB000]/5 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 relative">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4">
            // A NOTE FOR BURDITT · FROM MANUEL · JUNE 2026
          </div>
          <div className="flex items-center gap-3 mb-4">
            <Zap className="w-6 h-6 text-[#FFB000]" />
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-400">
              FOR BURDITT STRATEGIC PLANNING · ENERGY CHANNEL
            </div>
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            You asked how much your client can <span className="text-[#FFB000]">sell</span> their energy for. <span className="text-[#FFB000]">Here's the math.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            When we met today, the Ventia demo didn't land because your client is in energy, not industrial services. The question you actually asked is the one that matters: <i>"My client consumes 43 million kilowatt-hours a year at 22 cents — how much can he SELL it for?"</i>
          </p>
          <p className="mt-4 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            That question reframes energy from a cost line to a revenue line, and it's the right reframe. The honest answer: <b>between A$460,000 and A$1.75 million a year</b>, depending on how many of five arbitrage avenues your client stacks. The first three need zero capital outlay. The fourth depends on their existing PPA terms. The fifth requires a capex-with-payback decision the board would need to approve.
          </p>
          <p className="mt-4 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            DARA's job is to be the daily intelligence layer that tells the operations team when to act on each avenue. The mock brief at the bottom of this page shows you exactly what your client would receive every weekday morning, with the math run on their specific 43 GWh / A$9.46M-a-year scenario.
          </p>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <a
              href="#brief"
              data-testid="burditt-hero-brief-cta"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              <FileText className="w-4 h-4" /> See the mock brief <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#engage"
              data-testid="burditt-hero-engage-cta"
              className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-[#FFB000] underline-offset-4 hover:underline flex items-center gap-1.5"
            >
              <ChevronRight className="w-3.5 h-3.5" /> Reply to Manuel
            </a>
          </div>
        </div>
      </section>

      {/* HEADLINE STAT STRIP */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900" data-testid="burditt-stat-strip">
            <StatBlock label="Your client's annual spend" value="A$9.46M" sub="43 GWh × A$0.22/kWh" />
            <StatBlock label="No-capex recoverable" value="A$260k – A$1.15M" sub="avenues 01-04 / year" highlight />
            <StatBlock label="With-capex recoverable" value="A$460k – A$1.75M" sub="full stack / year" highlight />
            <StatBlock label="Percentage of bill" value="5% – 26%" sub="revenue recovery" />
          </div>
        </div>
      </section>

      {/* THE 5 AVENUES */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // FIVE ARBITRAGE AVENUES · STACKABLE · INDEPENDENT
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            Each avenue stands alone. <span className="text-[#FFB000]">Combine them and the stack effect compounds.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            None of these are theoretical. All five are live mechanisms in the AU National Electricity Market today. The ranges below are realistic for a medium-large industrial site with 2-4 megawatts of flexible load — which a 4.9 megawatt average demand profile (43 GWh ÷ 8760 hours) supports comfortably.
          </p>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="burditt-avenues-grid">
            {ARBITRAGE_AVENUES.map((av) => (
              <AvenueBlock key={av.code} {...av} />
            ))}
          </div>
        </div>
      </section>

      {/* DOWNLOAD MOCK BRIEF */}
      <section id="brief" className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // THE MOCK BRIEF · WHAT YOUR CLIENT WOULD RECEIVE EVERY MORNING
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            A sample of the actual brief — <span className="text-[#FFB000]">with the arbitrage stack visualised.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            Two-page A4 PDF. Hero element is the arbitrage stack chart, anchored on your client's actual numbers (43 GWh × A$0.22/kWh = A$9.46 million). Plus a mock day-of recommended-action list, market watch covering spot / FCAS / LGC / ACCU, 12-month opportunity forecast by avenue, and an operational + regulatory weekly priority strip. Hand it to your client to give them something tangible to react to.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 items-center">
            <a
              href="/api/public/sample-brief/energy-arbitrage-brief"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="burditt-download-brief"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              <FileText className="w-4 h-4" /> Download the energy arbitrage brief (PDF)
            </a>
            <span className="font-mono text-[11px] text-zinc-500">
              Two-page A4 · ~10KB · mock data · shareable
            </span>
          </div>
        </div>
      </section>

      {/* PARTNERSHIP STRUCTURE — handshake first */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // STRATEGIC PARTNER STRUCTURE · HANDSHAKE FIRST · PAPER LATER
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            No contract. No commission cap. <span className="text-[#FFB000]">Bring wins, we figure out fair on the back end.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            You move serious clients and the relationships you bring are not the kind of introductions that should be capped at a fixed commission bracket. The structure below is built so you're rewarded for actual outcomes — and so you have nothing to commit to at the start. Bring the first paid client, we sit down after the close and agree what's fair.
          </p>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="burditt-stages-grid">
            <StageBlock
              tag="STAGE 1"
              title="Open Partnership · no contract, no cap"
              trigger="From day one · bring an energy client to DARA"
              terms="If we close, you're publicly named as the strategic partner who introduced them. Once that first paid POC closes, you and I sit down and agree what's fair — could be cash, could be equity, could be a board observer seat, could be a revenue share. Whichever fits the outcome and what you want from the relationship. Bring a Tier 1 close, we figure out fair for that. Bring a Tier 3 close, we figure out big fair. No bracket I picked before knowing what you delivered."
              testid="burditt-stage-1"
            />
            <StageBlock
              tag="STAGE 2"
              title="Formal Strategic Partner · papered up"
              trigger="Triggered at 5 closed paying clients (the bar we agreed in person)"
              terms="Whatever we agreed at the end of Stage 1 gets papered properly under WA jurisdiction. Non-Executive Advisor-level access. Named publicly across the DARA website + decks + investor materials. Monthly call. If at this stage you want equity, that's your call and we structure it (vesting, cliff, advisor agreement). If you want cash, also fine. If you want both, also fine. Your shape."
              testid="burditt-stage-2"
            />
            <StageBlock
              tag="STAGE 3"
              title="Named Founding Partner"
              trigger="Triggered when you've helped DARA pass A$1M ARR"
              terms="Recognition tier. Public listing as Founding Strategic Partner across every DARA surface — site, decks, investor materials, the kind of relationship that gets named in the eventual IPO prospectus. Material upside in whichever form we negotiated at Stage 2."
              testid="burditt-stage-3"
            />
          </div>

          <p className="mt-8 text-sm text-zinc-400 max-w-3xl leading-relaxed italic">
            All three stages are reversible at any point if it isn't working for either side. No contract in your way at Stage 1 — you can start introducing tomorrow without sending anything to a lawyer.
          </p>
        </div>
      </section>

      {/* THE ASK */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // THE ASK · WHAT I'D LIKE FROM YOU
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            One conversation with your client. <span className="text-[#FFB000]">Show them this brief.</span>
          </h2>
          <p className="mt-6 text-base text-zinc-300 max-w-3xl leading-relaxed">
            The simplest next step is to forward your client the downloadable brief above and ask them which of the five avenues they would most like to understand in depth. Their answer tells us where the real conversation starts. We don't need to commit to anything beyond that until they react to the artefact.
          </p>
          <p className="mt-4 text-base text-zinc-300 max-w-3xl leading-relaxed">
            If after they read it they want to talk numbers, I'm available for a 30-minute call. If they want to walk it through with their Operations Engineer or CFO first, also fine — that's a longer cycle but the right one. No pressure on timeline from my end.
          </p>
        </div>
      </section>

      {/* ENGAGE */}
      <section id="engage" className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
                // REPLY TO MANUEL · NOTHING SENT TO ANYONE ELSE
              </div>
              <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight leading-tight">
                Drop a note. <span className="text-[#FFB000]">I'll write back within one business day.</span>
              </h2>
              <p className="mt-4 text-sm sm:text-base text-zinc-300 leading-relaxed">
                This form goes only to me. Not a sales team. Not a CRM dump. Just the two of us continuing the conversation we started in person.
              </p>
              <p className="mt-4 text-xs sm:text-[11px] text-zinc-500 leading-relaxed italic">
                If you'd rather skip the form, you have my number from the meeting. Either works.
              </p>
            </div>

            {submitted ? (
              <div className="border border-[#FFB000]/40 bg-[#FFB000]/5 p-8" data-testid="burditt-engage-success">
                <CheckCircle2 className="w-10 h-10 text-[#FFB000] mb-4" />
                <h3 className="font-heading font-bold text-xl tracking-tight mb-2">Received.</h3>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  Thanks. I'll write back within one business day. If anything urgent comes up before then, my mobile is in your inbox already.
                </p>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                  // Manuel · DARA OS
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="burditt-engage-form">
                <Field label="Work email" required>
                  <input
                    type="email"
                    required
                    data-testid="burditt-form-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                    placeholder="you@firm.com.au"
                  />
                </Field>
                <Field label="Your name">
                  <input
                    type="text"
                    data-testid="burditt-form-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <Field label="Role / firm">
                  <input
                    type="text"
                    data-testid="burditt-form-role"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <Field label="What you'd like to discuss">
                  <input
                    type="text"
                    data-testid="burditt-form-focus"
                    value={form.focus}
                    onChange={(e) => setForm({ ...form, focus: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <Field label="Anything else" sub="Optional — client context, push-back, questions">
                  <textarea
                    rows={4}
                    data-testid="burditt-form-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="burditt-form-submit"
                  className="w-full bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Reply to Manuel →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <BespokeWatermark pageLabel="BURDITT" position="bottom" />

      <footer className="bg-black border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <p className="text-xs sm:text-[11px] text-zinc-500 leading-relaxed font-mono">
            DARA OS · Manuel Maji · majisonmanuel@gmail.com · +61 481 172 655 · Perth WA
          </p>
          <p className="mt-2 text-xs sm:text-[11px] text-zinc-600 leading-relaxed italic">
            Bespoke page · prepared for Burditt Strategic Planning · not indexed · not linked from public surfaces · every visit is logged and traceable to the session that received this URL.
          </p>
        </div>
      </footer>
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────

function StatBlock({ label, value, sub, highlight }) {
  return (
    <div className={`bg-black p-5 sm:p-6 flex flex-col ${highlight ? "border-t-2 border-t-[#FFB000]" : ""}`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
        {label}
      </div>
      <div className={`font-heading font-black text-xl sm:text-2xl tracking-tight ${highlight ? "text-[#FFB000]" : "text-white"}`}>
        {value}
      </div>
      <div className="mt-1 font-mono text-[10px] text-zinc-500 leading-relaxed">{sub}</div>
    </div>
  );
}

function AvenueBlock({ Icon, code, name, plain, revenue, capex, timeline }) {
  return (
    <div className="bg-black p-6 sm:p-8 flex flex-col" data-testid={`burditt-avenue-${code}`}>
      <div className="flex items-center gap-3 mb-3">
        <Icon className="w-5 h-5 text-[#FFB000]" />
        <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000]">
          // {code}
        </span>
      </div>
      <h3 className="font-heading font-bold text-xl tracking-tight mb-3">{name}</h3>
      <p className="text-sm text-zinc-300 leading-relaxed mb-4">{plain}</p>
      <div className="border-t border-zinc-900 pt-4 grid grid-cols-3 gap-3 mt-auto">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">// REVENUE</div>
          <p className="text-xs sm:text-[13px] text-[#FFB000] font-bold leading-snug">{revenue}</p>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">// CAPEX</div>
          <p className="text-xs sm:text-[13px] text-zinc-200 leading-snug">{capex}</p>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">// TIME</div>
          <p className="text-xs sm:text-[13px] text-zinc-200 leading-snug">{timeline}</p>
        </div>
      </div>
    </div>
  );
}

function StageBlock({ tag, title, trigger, terms, testid }) {
  return (
    <div className="bg-black p-6 sm:p-8 flex flex-col" data-testid={testid}>
      <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
        // {tag}
      </div>
      <h3 className="font-heading font-bold text-xl tracking-tight mb-3">{title}</h3>
      <div className="border-t border-zinc-900 pt-3 mb-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">// TRIGGERED WHEN</div>
        <p className="text-sm text-zinc-300 leading-relaxed">{trigger}</p>
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">// TERMS</div>
        <p className="text-sm text-zinc-300 leading-relaxed">{terms}</p>
      </div>
    </div>
  );
}

function Field({ label, sub, required, children }) {
  return (
    <div>
      <label className="block font-mono text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5">
        {label}{required && <span className="text-[#FFB000] ml-1">*</span>}
        {sub && <span className="text-zinc-600 ml-2 normal-case tracking-normal italic">{sub}</span>}
      </label>
      {children}
    </div>
  );
}
